import * as React from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import strings from './../utils/StringManager';
import { IDictionaryItem, useGetDictionaryApi } from '../services/useDictionariesApi';
import { Typography } from '@material-ui/core';
import clsx from 'clsx';

export interface OptionType {
    value: string;
    label: string;
}

function dataMapper(item: IDictionaryItem | string): OptionType {

    if (!item)
        return null;

    if (typeof item === "string") {
        return {
            label: item,
            value: item
        };
    }
    else
    {
        return {
            label: item.Name,
            value: item.Id
        };
    }
}

function valueMapper(item: OptionType, dicType: string): IDictionaryItem {

    if (!item)
        return null;

    return {
        Id: item.value,
        Name: item.label,

        Description: null,
        Config: null,
        DictionaryType: dicType,
        NameEn: item.label,
        NamePl: item.label,
        Visualization: null,
        BaseUrl: null,
    };
}

interface IProps {
    className?: string,
    dictionaryName: string,
    parentDictionaryId?: string,
    value: OptionType,
    onChange: (value: OptionType) => void,

    disabled?: boolean,
    error?: boolean,
    helperText?: string,
    label?: string,
}

export default function DictionaryAutocomplete(props: IProps) {

    const [setModel, result, isLoading, isError] = useGetDictionaryApi();
    const [open, setOpen] = React.useState(false);
    const [options, setOptions] = React.useState<IDictionaryItem[]>([]);
    const [init, setInit] = React.useState(false);

    const [value, setValue] = React.useState<IDictionaryItem>(() => valueMapper(props.value, props.dictionaryName));
   
    React.useEffect(() => {
        if (!open || init) {
            return undefined;
        }
        setInit(true);
        setModel({
            dictionaryName: props.dictionaryName,
            parentDictionaryId: props.parentDictionaryId,
        })

    }, [open]);

    React.useEffect(() => {
        if (!init) {
            return undefined;
        }
        setModel({
            dictionaryName: props.dictionaryName,
            parentDictionaryId: props.parentDictionaryId,
        });
    }, [props.parentDictionaryId]);

    React.useEffect(() => {
        if (result) {
            setOptions(result);
        }
    }, [result]);

    React.useEffect(() => {
        setValue(valueMapper(props.value, props.dictionaryName));
    }, [props.value]);

    return (
        <Autocomplete
            className={clsx("mi-lab-autocomplete-outlined", props.className)}
            value={value}
            onChange={(event, newValue) => {
                props.onChange(dataMapper(newValue));
            }}
            open={open}
            onOpen={() => {
                setOpen(true);
            }}
            onClose={() => {
                setOpen(false);
            }}
            getOptionSelected={(option, value) => option.Id === value.Id}
            getOptionLabel={option => option.Name}
            options={options}
            loading={isLoading}
            disabled={props.disabled}

            clearText={strings.get("clear")}
            noOptionsText={strings.get("noOptionsMessage")}
            openText={strings.get("open")}
            closeText={strings.get("close")}
            loadingText={strings.get("loading") + "..."}

            renderInput={params => (
                <TextField
                    {...params}
                    variant="outlined"
                    error={props.error}
                    helperText={props.helperText}
                    fullWidth
                    label={props.label}
                    InputProps={{
                        ...params.InputProps,
                        autoComplete: 'disabled',
                        endAdornment: (
                            <React.Fragment>
                                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                />
            )}

            renderOption={(option) => {
                return <div>
                    {option.Name}
                    <Typography variant="body2" color="textSecondary">
                        {option.Description}
                    </Typography>
                </div>
            }}
        />
    );
}

interface IDictionaryMultipleAutocompleteProps {
    
    dictionaryName: string,
    parentDictionaryId?: string,
    value: OptionType[],
    onChange: (value: OptionType[]) => void,

    className?: string,
    disabled?: boolean,
    error?: boolean,
    helperText?: string,
    label?: string,
}


export function DictionaryMultipleAutocomplete(props: IDictionaryMultipleAutocompleteProps) {

    const [setModel, result, isLoading, isError] = useGetDictionaryApi();
    const [open, setOpen] = React.useState(false);
    const [options, setOptions] = React.useState<IDictionaryItem[]>([]);
    const [init, setInit] = React.useState(false);

    const [value, setValue] = React.useState<IDictionaryItem[]>([]);

    React.useEffect(() => {
        if (!open || init) {
            return undefined;
        }
        setInit(true);
        setModel({
            dictionaryName: props.dictionaryName,
            parentDictionaryId: props.parentDictionaryId,
        })

    }, [open]);

    React.useEffect(() => {
        if (!init) {
            return undefined;
        }
        setModel({
            dictionaryName: props.dictionaryName,
            parentDictionaryId: props.parentDictionaryId,
        });
    }, [props.parentDictionaryId]);

    React.useEffect(() => {
        if (result) {
            setOptions(result);
        }
    }, [result]);

    React.useEffect(() => {
        const v = props.value.map(x => valueMapper(x, props.dictionaryName));
        setValue(v);
    }, [props.value]);

    return (
        <Autocomplete
            multiple

            className={clsx("mi-lab-autocomplete-outlined", props.className)}
            value={value}
            onChange={(event, newValue) => {
                if (Array.isArray(newValue)) {
                    props.onChange(newValue.map(x => dataMapper(x)));
                    return;
                }
                props.onChange([]);
            }}
            open={open}
            onOpen={() => {
                setOpen(true);
            }}
            onClose={() => {
                setOpen(false);
            }}
            getOptionSelected={(option, value) => option.Id === value.Id}
            getOptionLabel={option => option.Name}
            options={options}
            loading={isLoading}
            disabled={props.disabled}

            clearText={strings.get("clear")}
            noOptionsText={strings.get("noOptionsMessage")}
            openText={strings.get("open")}
            closeText={strings.get("close")}
            loadingText={strings.get("loading") + "..."}

            renderInput={params => (
                <TextField
                    {...params}
                    variant="outlined"
                    error={props.error}
                    helperText={props.helperText}
                    fullWidth
                    label={props.label}
                    InputProps={{
                        ...params.InputProps,
                        autoComplete: 'disabled',
                        endAdornment: (
                            <React.Fragment>
                                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                />
            )}

            renderOption={(option) => {
                return <div>
                    {option.Name}
                    <Typography variant="body2" color="textSecondary">
                        {option.Description}
                    </Typography>
                </div>
            }}
        />
    );
}